import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { useAuthState } from "@/app/useAuthState";
import type { User } from "@/app/types";
import { AppShell } from "@/components/layout/AppShell";
import { AdminPage } from "@/pages/AdminPage";
import { DashboardPage, NoAppAccessCard } from "@/pages/DashboardPage";
import { LandingPage } from "@/pages/LandingPage";
import { NotFoundPage } from "@/pages/NotFoundPage";
import { ScreenBudgetAdminPage } from "@/services/screen-budget/web/ScreenBudgetAdminPage";
import { ScreenBudgetPage } from "@/services/screen-budget/web/ScreenBudgetPage";
import "./index.css";

interface GuardProps {
  isLoading: boolean;
  user: User | null;
  children: ReactNode;
}

function RouteLoading() {
  return (
    <div className="min-h-screen">
      <main className="container mx-auto grid min-h-screen max-w-3xl place-items-center px-6 py-12">
        <p className="text-sm text-muted-foreground">Loading session...</p>
      </main>
    </div>
  );
}

function PublicOnlyRoute({ isLoading, user, children }: GuardProps) {
  if (isLoading) return <RouteLoading />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function RequireAuthRoute({ isLoading, user, children }: GuardProps) {
  if (isLoading) return <RouteLoading />;
  if (!user) return <Navigate to="/" replace />;
  return <>{children}</>;
}

interface RequireAdminRouteProps extends GuardProps {
  redirectTo?: string;
}

function RequireAdminRoute({ isLoading, user, children, redirectTo = "/dashboard" }: RequireAdminRouteProps) {
  if (isLoading) return <RouteLoading />;
  if (!user) return <Navigate to="/" replace />;
  if (!user.isAdmin) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}

export function App() {
  const auth = useAuthState();

  const handleLogin = async (username: string, password: string) => {
    await auth.login(username, password);
  };

  const handleUnauthorized = () => {
    auth.clearAuth();
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <PublicOnlyRoute isLoading={auth.isLoadingUser} user={auth.user}>
              <LandingPage
                isSubmitting={auth.isAuthSubmitting}
                error={auth.authError}
                onLogin={handleLogin}
              />
            </PublicOnlyRoute>
          }
        />

        <Route
          path="/dashboard"
          element={
            <RequireAuthRoute isLoading={auth.isLoadingUser} user={auth.user}>
              {auth.user ? (
                <AppShell user={auth.user} accessibleApps={auth.accessibleApps} onLogout={auth.logout}>
                  {auth.user.isAdmin && auth.isLoadingApps ? (
                    <RouteLoading />
                  ) : auth.user.isAdmin ? (
                    <DashboardPage apps={auth.accessibleApps} />
                  ) : auth.isLoadingApps ? (
                    <RouteLoading />
                  ) : auth.defaultAppPath ? (
                    <Navigate to={auth.defaultAppPath} replace />
                  ) : (
                    <NoAppAccessCard />
                  )}
                </AppShell>
              ) : null}
            </RequireAuthRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <RequireAuthRoute isLoading={auth.isLoadingUser} user={auth.user}>
              <RequireAdminRoute isLoading={auth.isLoadingUser} user={auth.user}>
                {auth.user ? (
                <AppShell user={auth.user} accessibleApps={auth.accessibleApps} onLogout={auth.logout}>
                  <AdminPage currentUser={auth.user} onUnauthorized={handleUnauthorized} />
                </AppShell>
                ) : null}
              </RequireAdminRoute>
            </RequireAuthRoute>
          }
        />

        <Route
          path="/apps/screen-budget"
          element={
            <RequireAuthRoute isLoading={auth.isLoadingUser} user={auth.user}>
              {auth.user ? (
                <AppShell user={auth.user} accessibleApps={auth.accessibleApps} onLogout={auth.logout}>
                  <ScreenBudgetPage onUnauthorized={handleUnauthorized} isAdmin={auth.user.isAdmin} />
                </AppShell>
              ) : null}
            </RequireAuthRoute>
          }
        />

        <Route
          path="/apps/screen-budget/admin"
          element={
            <RequireAuthRoute isLoading={auth.isLoadingUser} user={auth.user}>
              <RequireAdminRoute isLoading={auth.isLoadingUser} user={auth.user} redirectTo="/dashboard">
                {auth.user ? (
                  <AppShell user={auth.user} accessibleApps={auth.accessibleApps} onLogout={auth.logout}>
                    <ScreenBudgetAdminPage onUnauthorized={handleUnauthorized} currentUserId={auth.user.id} />
                  </AppShell>
                ) : null}
              </RequireAdminRoute>
            </RequireAuthRoute>
          }
        />

        <Route path="*" element={<NotFoundPage user={auth.user} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
